import { IAdvertiser, IAdvertiserDomain, IAdvertiserState } from "advertiser/model"
import { IAgency, IAgencyState } from "agency/model"
import { IAudience, IAudienceState } from "audience/model"
import { ICampaignState, ICampaignType } from "campaign/model"
import { ICurrency, ICurrencyState } from "currency/model"
import { IDsp, IDspState } from "dsp/model"
import { IEnvironmentData, IEnvironmentState } from "environments/model"
import { IFormState } from "form/model"
import { IFormat, IFormatState } from "format/model"
import { BrowserHistory } from "history"
import { IMediaState, IMediaType } from "media/model"
import { IAppMessageState } from "message/model"
import { INotificationState } from "notification/model"
import { IOperator } from "operator/model"
import { IOrder, IOrderState } from "order/model"
import { IPageState } from "page/model"
import { IReportsState } from "reports/model"
import { IScheduleState, IWeather } from "schedule/model"
import { ISsp, ISspState } from "ssp/model"
import { IUiState } from "ui/model"
import { ITagState, IUserTag } from "user-tag/model"
import { IUserInfo, IUserState } from "user/model"
import { IEventModule } from "./event/model"
import { IHistoryListenerItem } from "./history/model"
import { ILanguageState, ITranslationsByLocale, LOCALES } from "./language/model"
import { IDateService } from "./util/date/model"
import { ILocalStorageService } from "./util/local-storage/model"

export const NOT_SAVED_ID: number = -1
export const MAX_FIELD_LENGTH: number = 255

export const CARRIER_RETURN: string = "\r\n"

export type IFileUploadOnProgressFn = (progression: IUploadProgress) => any

export enum APP_EVENTS {
  STORE_READY = "STORE_READY",
  APP_READY = "APP_READY",
  WINDOW_RESIZE = "WINDOW_RESIZE",
  APP_CONTEXTS_CHANGE = "APP_CONTEXTS_CHANGE"
}

export enum APP_CONTEXTS {
  CAMPAIGN_CREATE = "campaign-create",
  CAMPAIGN_EDIT = "campaign-edit",
  FORECAST_CREATE = "forecast-create",
  FORECAST_EDIT = "forecast-edit",
  DSP_DEAL_CREATE = "dsp-deal-create",
  DSP_DEAL_EDIT = "dsp-deal-edit"
}

export const ALL_IMAGE_MIME_TYPES: string[] = [
  "image/apng",
  "image/avif",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/svg+xml"
]

export const ALL_ICO_MIME_TYPES: string[] = ["image/ico", "image/x-icon", "image/vnd.microsoft.icon"]

export const ALL_VIDEO_MIME_TYPES = [
  "video/animaflex",
  "video/x-flv",
  "video/mp4",
  "application/x-mpegURL",
  "video/MP2T",
  "video/3gpp",
  "video/quicktime",
  "video/x-msvideo",
  "video/msvideo",
  "video/avi",
  "video/avs-video",
  "video/x-ms-wmv",
  "video/webm",
  "video/ogg",
  "application/x-troff-msvideo",
  "application/x-mplayer2",
  "video/x-ms-asf",
  "video/x-ms-asf-plugin",
  "video/x-dv",
  "video/dl",
  "video/x-dl",
  "video/x-dv",
  "video/fli",
  "video/x-fli",
  "video/x-atomic3d-feature",
  "video/gl",
  "video/x-gl",
  "video/mpeg",
  "video/x-sgi-movie",
  "video/x-mpeg",
  "video/x-mpeq2a",
  "video/x-qtc",
  "video/vnd.rn-realvideo",
  "video/x-scm",
  "video/vdo",
  "video/vivo",
  "video/vnd.vivo",
  "video/vosaic"
]

export type IAppDataLoader = (params: IAppInitParams, onFinish: (error?: string) => any) => any

export interface IRootState {
  app: IAppState
  ui: IUiState
  language: ILanguageState
  user: IUserState
  message: IAppMessageState
  page: IPageState
  campaign: ICampaignState
  agency: IAgencyState
  order: IOrderState
  advertiser: IAdvertiserState
  currency: ICurrencyState
  audience: IAudienceState
  environments: IEnvironmentState
  ssp: ISspState
  form: IFormState
  media: IMediaState
  tag: ITagState
  format: IFormatState
  reports: IReportsState
  schedule: IScheduleState
  notification: INotificationState
  dsp: IDspState
}

export interface IUtilService {
  Date: IDateService
  LocalStorage: ILocalStorageService
  loadJSFile(fileUrl: string): Promise<void>
}

export interface IAppServiceState {
  history: {
    history: BrowserHistory
    currentHash: string
    currentHashParameters: { [key: string]: string }
    allListeners: IHistoryListenerItem[]
    nextListenerId: number
  }
  language: {
    translationsByLocale: ITranslationsByLocale
    defaultLocale?: LOCALES
  }
  localStorageAvailable: boolean
  restBaseUrl: string | undefined
}

export interface IAppEventModule extends IEventModule {
  on: {
    storeReady(listenerId: string, fn: () => void): void
    appReady(listenerId: string, fn: () => void): void
    windowResize(listenerId: string, fn: () => void): void
    appContextChange(listenerId: string, fn: (context: APP_CONTEXTS | null) => void): void
  }
}

export interface IAppInitParams {
  userInfo: IUserInfo
  statuses: string[]
  types: ICampaignType[]
  currencies: ICurrency[]
  environments: IEnvironmentData
  ssps: ISsp[]
  audiences: IAudience[]
  orders: IOrder[]
  agencies: IAgency[]
  advertisers: IAdvertiser[]
  advertiserDomains: IAdvertiserDomain[]
  operators: IOperator[]
  mediaTypes: IMediaType[]
  userTags: IUserTag[]
  formats: IFormat[]
  weathers: IWeather[]
  dsps: IDsp[]
}

export interface IAppState {
  activeContext: APP_CONTEXTS | null
  isInitializating: boolean
  hasInitError: boolean
}

export interface ICSVRow {
  [key: string]: string
}

export interface IUploadProgress {
  loadedBytes: number
  totalBytes: number
}

export interface ICsvFileColumn {
  key: string
  label: string
}

export type ISortOrder = "asc" | "desc"

export interface IServerPagination {
  page: number // start at 0
  pageSize: number // row per page
  orderBy?: string
  order?: ISortOrder
}
